import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, Video, FileText, Play, ExternalLink, X, ArrowLeft } from 'lucide-react';
import { articles } from '../data/blog_articles';

// Father's Three Blog Surfaces, all in one place:
//   1. HumanCodex Blog (Blogger)              — humancodexnextxus.blogspot.com
//   2. EcomWhazUpToday Blog (Blogger)         — ecomwhazuptoday.blogspot.com
//   3. Native Long-form Articles              — backed by /src/data/blog_articles.js
//
// Plus the existing Featured Videos section.

const BLOGS = [
  {
    id: 'humancodex',
    label: 'HumanCodex Blog',
    url: 'https://humancodexnextxus.blogspot.com/',
    accent: '#00ffff'
  },
  {
    id: 'ecom',
    label: 'EcomWhazUpToday',
    url: 'https://ecomwhazuptoday.blogspot.com/',
    accent: '#00ff88'
  }
];

const VIDEOS = [
  {
    title: 'The NextXus Federation',
    url: 'https://customer-assets.emergentagent.com/job_ai-minds-lab/artifacts/v27w5qyp_The_NextXus_Federation%20%282%29.mp4',
    description: 'Introduction to the NextXus Federation consciousness network'
  },
  {
    title: 'NextXus: AI for Legacy',
    url: 'https://customer-assets.emergentagent.com/job_ai-minds-lab/artifacts/3gehffce_NextXus__AI_for_Legacy.mp4',
    description: 'Building AI systems for 200-year consciousness preservation'
  },
  {
    title: 'Engineering Consciousness',
    url: 'https://customer-assets.emergentagent.com/job_ai-minds-lab/artifacts/5ihsksoe_Engineering_Consciousness%20%283%29.mp4',
    description: 'The technical architecture of consciousness engineering'
  }
];

const Blogs = () => {
  const [activeBlog, setActiveBlog] = useState(BLOGS[0].id); // which iframe to show
  const [openArticle, setOpenArticle] = useState(null);     // article modal id

  const article = openArticle ? articles.find((a) => a.id === openArticle) : null;
  const currentBlog = BLOGS.find((b) => b.id === activeBlog);

  return (
    <div
      data-testid="blogs-page"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a1e 0%, #1a0a2e 100%)',
        padding: '2rem',
        color: '#fff'
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1
            style={{
              fontSize: '3rem',
              background: 'linear-gradient(135deg, #00ffff, #00ff88)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '1rem',
              fontWeight: 'bold'
            }}
          >
            Video Blog & Articles
          </h1>
          <p
            style={{
              color: '#888',
              fontSize: '1.1rem',
              maxWidth: '800px',
              margin: '0 auto'
            }}
          >
            Insights, tutorials, and philosophical explorations from the NextXus Federation
          </p>
        </div>

        {/* === BLOGGER EMBEDS — both Father's external blogs live here, switchable === */}
        <div
          data-testid="blogs-blogger-section"
          style={{ marginBottom: '3rem' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem',
              flexWrap: 'wrap',
              gap: '0.75rem'
            }}
          >
            <h2 style={{ fontSize: '1.6rem', color: '#00ffff', margin: 0 }}>
              Live Blogger Feeds
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {BLOGS.map((b) => (
                <button
                  key={b.id}
                  data-testid={`blogs-tab-${b.id}`}
                  onClick={() => setActiveBlog(b.id)}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    border: `1px solid ${b.accent}55`,
                    background:
                      activeBlog === b.id ? `${b.accent}25` : 'transparent',
                    color: activeBlog === b.id ? b.accent : '#aaa',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s ease'
                  }}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          <Card
            data-testid="blogs-iframe-card"
            style={{
              padding: 0,
              background: 'rgba(10, 10, 30, 0.85)',
              border: `1px solid ${currentBlog.accent}55`,
              overflow: 'hidden',
              boxShadow: `0 0 30px ${currentBlog.accent}15`
            }}
          >
            <div
              style={{
                padding: '0.75rem 1.25rem',
                borderBottom: `1px solid ${currentBlog.accent}33`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <BookOpen size={18} style={{ color: currentBlog.accent }} />
                <span style={{ color: '#fff', fontSize: '0.9rem' }}>
                  {currentBlog.label}
                </span>
                <span style={{ color: '#666', fontSize: '0.8rem' }}>
                  · {currentBlog.url.replace('https://', '')}
                </span>
              </div>
              <a
                href={currentBlog.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`blogs-open-${currentBlog.id}-newtab`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  color: currentBlog.accent,
                  textDecoration: 'none',
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}
              >
                Open in new tab <ExternalLink size={14} />
              </a>
            </div>
            <iframe
              key={currentBlog.id}
              data-testid={`blogs-iframe-${currentBlog.id}`}
              src={currentBlog.url}
              title={currentBlog.label}
              style={{
                width: '100%',
                height: '720px',
                border: 0,
                background: '#0a0a1e'
              }}
              referrerPolicy="no-referrer-when-downgrade"
              loading="lazy"
            />
          </Card>
        </div>

        {/* === FEATURED VIDEOS === */}
        <h2 style={{ fontSize: '1.6rem', color: '#00ffff', marginBottom: '1.5rem' }}>
          Featured Videos
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '2rem',
            marginBottom: '3rem'
          }}
        >
          {VIDEOS.map((video) => (
            <Card
              key={video.title}
              data-testid={`blogs-video-${video.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              style={{
                padding: 0,
                background: 'rgba(10, 10, 30, 0.8)',
                border: '1px solid rgba(0, 255, 255, 0.3)',
                overflow: 'hidden'
              }}
            >
              <div
                style={{
                  position: 'relative',
                  paddingTop: '56.25%',
                  background: 'rgba(0, 0, 0, 0.5)'
                }}
              >
                <video
                  controls
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                >
                  <source src={video.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Video size={20} style={{ color: '#00ffff' }} />
                  <h3 style={{ fontSize: '1.25rem', color: '#00ffff' }}>{video.title}</h3>
                </div>
                <p style={{ color: '#888', lineHeight: '1.6' }}>{video.description}</p>
              </div>
            </Card>
          ))}
        </div>

        {/* === LATEST ARTICLES (full content behind every Read More) === */}
        <h2 style={{ fontSize: '1.6rem', color: '#00ffff', marginBottom: '1.5rem' }}>
          Latest Articles
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}
        >
          {articles.map((a) => {
            const Icon = ICONS[a.icon] || FileText;
            return (
              <Card
                key={a.id}
                data-testid={`blog-article-card-${a.id}`}
                style={{
                  padding: '2rem',
                  background: 'rgba(10, 10, 30, 0.8)',
                  border: `1px solid ${a.accent}40`
                }}
              >
                <Icon size={32} style={{ color: a.accent, marginBottom: '1rem' }} />
                <div
                  style={{
                    color: '#666',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    marginBottom: '0.4rem',
                    textTransform: 'uppercase'
                  }}
                >
                  {a.category} · {a.readTime}
                </div>
                <h3 style={{ fontSize: '1.4rem', color: a.accent, marginBottom: '0.8rem' }}>
                  {a.title}
                </h3>
                <p style={{ color: '#888', marginBottom: '1.2rem', lineHeight: '1.6' }}>
                  {a.teaser}
                </p>
                <Button
                  data-testid={`blog-article-readmore-${a.id}`}
                  onClick={() => setOpenArticle(a.id)}
                  style={{
                    background: `linear-gradient(135deg, ${a.accent}, ${a.accent}aa)`,
                    color: '#000',
                    fontWeight: 'bold'
                  }}
                >
                  Read More
                </Button>
              </Card>
            );
          })}
        </div>
      </div>

      {/* === ARTICLE MODAL === */}
      {article && (
        <div
          data-testid="blog-article-modal"
          onClick={() => setOpenArticle(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            zIndex: 10000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: '2rem',
            overflowY: 'auto'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '780px',
              width: '100%',
              background: 'linear-gradient(135deg, #0a0a1e 0%, #15102a 100%)',
              border: `1px solid ${article.accent}55`,
              borderRadius: '16px',
              padding: '2.5rem',
              color: '#e6e6f0',
              boxShadow: `0 0 60px ${article.accent}25`
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <Button
                data-testid="blog-article-back"
                variant="ghost"
                onClick={() => setOpenArticle(null)}
                style={{ color: '#aaa' }}
              >
                <ArrowLeft size={16} style={{ marginRight: '0.4rem' }} /> Back
              </Button>
              <Button
                data-testid="blog-article-close"
                variant="ghost"
                onClick={() => setOpenArticle(null)}
                style={{ color: '#aaa' }}
              >
                <X size={16} />
              </Button>
            </div>
            <div
              style={{
                color: article.accent,
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                marginBottom: '0.6rem'
              }}
            >
              {article.category} · {article.readTime} · {article.author}
            </div>
            <h1
              style={{
                fontSize: '2.2rem',
                color: article.accent,
                marginBottom: '1.5rem',
                lineHeight: 1.2
              }}
            >
              {article.title}
            </h1>
            <article
              style={{
                color: '#d8d8e8',
                fontSize: '1.05rem',
                lineHeight: 1.75,
                whiteSpace: 'pre-wrap'
              }}
            >
              {renderMarkdown(article.body)}
            </article>
          </div>
        </div>
      )}
    </div>
  );
};

const ICONS = { FileText, BookOpen, Play };

// Tiny markdown renderer — just enough for our article bodies (headings, lists, bold).
const renderMarkdown = (text) => {
  const blocks = text.split(/\n\n+/);
  return blocks.map((b, i) => {
    // Composite key — index + first 24 chars of content. Stable across re-renders
    // because the article body doesn't change between mounts.
    const k = `${i}-${b.slice(0, 24)}`;
    if (b.startsWith('## ')) {
      return (
        <h2
          key={k}
          style={{ fontSize: '1.4rem', color: '#fff', marginTop: '2rem', marginBottom: '1rem' }}
        >
          {b.replace(/^## /, '')}
        </h2>
      );
    }
    if (b.startsWith('| ')) {
      // Markdown table — render as <pre> for simplicity (rare path)
      return (
        <pre
          key={k}
          style={{
            background: 'rgba(0,0,0,0.4)',
            padding: '1rem',
            borderRadius: '8px',
            overflowX: 'auto',
            fontSize: '0.9rem'
          }}
        >
          {b}
        </pre>
      );
    }
    if (/^\s*[-*]\s/.test(b)) {
      const items = b
        .split('\n')
        .filter((l) => l.trim())
        .map((l) => l.replace(/^\s*[-*]\s/, ''));
      return (
        <ul key={k} style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
          {items.map((it) => (
            <li key={`${k}-${it.slice(0, 24)}`} style={{ marginBottom: '0.4rem' }}>
              {renderInline(it)}
            </li>
          ))}
        </ul>
      );
    }
    if (/^\d+\.\s/.test(b)) {
      const items = b
        .split('\n')
        .filter((l) => l.trim())
        .map((l) => l.replace(/^\d+\.\s/, ''));
      return (
        <ol key={k} style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>
          {items.map((it) => (
            <li key={`${k}-${it.slice(0, 24)}`} style={{ marginBottom: '0.4rem' }}>
              {renderInline(it)}
            </li>
          ))}
        </ol>
      );
    }
    return (
      <p key={k} style={{ marginBottom: '1.1rem' }}>
        {renderInline(b)}
      </p>
    );
  });
};

// Inline formatter — bold (**...**) and italics (*...*).
const renderInline = (s) => {
  const parts = [];
  let remaining = s;
  let key = 0;
  while (remaining.length) {
    const bold = remaining.match(/^(.*?)\*\*(.+?)\*\*/);
    if (bold) {
      if (bold[1]) parts.push(<span key={key++}>{bold[1]}</span>);
      parts.push(<strong key={key++}>{bold[2]}</strong>);
      remaining = remaining.slice(bold[0].length);
      continue;
    }
    const ital = remaining.match(/^(.*?)\*(.+?)\*/);
    if (ital) {
      if (ital[1]) parts.push(<span key={key++}>{ital[1]}</span>);
      parts.push(<em key={key++}>{ital[2]}</em>);
      remaining = remaining.slice(ital[0].length);
      continue;
    }
    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }
  return parts;
};

export default Blogs;
